import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Check, X, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GameRulesEditableCardProps {
  emoji: string;
  title: string;
  value: number;
  isAdmin: boolean;
  onSave: (newValue: number) => Promise<boolean>;
  borderColorClass?: string;
  titleColorClass?: string;
  isCurrency?: boolean;
}

const GameRulesEditableCard: React.FC<GameRulesEditableCardProps> = ({
  emoji,
  title,
  value,
  isAdmin,
  onSave,
  borderColorClass = 'border-zinc-700',
  titleColorClass = 'text-white',
  isCurrency = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());
  const [isSaving, setIsSaving] = useState(false);

  const formatDisplayValue = (val: number) => {
    if (isCurrency) {
      return `₡${(val / 1000).toFixed(0)}k`;
    }
    return val.toString();
  };

  const handleSave = async () => {
    const numValue = parseInt(editValue, 10);
    if (isNaN(numValue) || numValue < 0) {
      setEditValue(value.toString());
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    const success = await onSave(numValue);
    setIsSaving(false);

    if (success) {
      setIsEditing(false);
    } else {
      setEditValue(value.toString());
    }
  };

  const handleCancel = () => {
    setEditValue(value.toString());
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className={cn("bg-zinc-950 p-3 rounded-lg border text-center relative", borderColorClass)}>
      <span className="text-xl">{emoji}</span>
      <h4 className={cn("font-bold text-xs mt-1", titleColorClass)}>{title}</h4>
      
      {isEditing ? (
        <div className="flex items-center justify-center gap-1 mt-1">
          <Input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-20 h-7 text-center bg-zinc-800 border-zinc-600 text-white text-sm"
            autoFocus
            disabled={isSaving}
          />
          <div className="flex flex-col gap-0.5">
            <Button
              size="icon"
              variant="ghost"
              className="h-5 w-5 text-green-400 hover:text-green-300 hover:bg-green-900/20"
              onClick={handleSave}
              disabled={isSaving}
            >
              <Check className="h-3 w-3" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-5 w-5 text-red-400 hover:text-red-300 hover:bg-red-900/20"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-1">
          <p className="text-lg font-bold text-white">{formatDisplayValue(value)}</p>
          {isAdmin && (
            <Button
              size="icon"
              variant="ghost"
              className="h-5 w-5 text-zinc-500 hover:text-blue-400 hover:bg-blue-900/20 absolute top-1 right-1"
              onClick={() => setIsEditing(true)}
            >
              <Pencil className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default GameRulesEditableCard;
